import { isDesktopRuntime } from '@/desktop/desktopDetection';
import {
  BUILT_IN_DEPLOYMENT_ADDRESS,
  effectiveDeploymentAddress,
  normalizeDeploymentAddress,
  readChosenDeploymentAddress,
  readReachedDeploymentAddress,
} from './deploymentAddress';

/**
 * A page a browser loaded from an origin can reach that origin.
 *
 * Not an assumption — an observation about how this code got here. It is what keeps the hosted web
 * app's first visit exactly as it was: no probe, no connect screen, straight to signing in.
 */
function servedFromItsOwnDeployment(): boolean {
  if (isDesktopRuntime()) return false;
  if (typeof window === 'undefined') return false;
  if (!BUILT_IN_DEPLOYMENT_ADDRESS) return true;
  return (
    normalizeDeploymentAddress(BUILT_IN_DEPLOYMENT_ADDRESS) ===
    normalizeDeploymentAddress(window.location.origin)
  );
}

/**
 * Does this start need somebody to say where the deployment is?
 *
 * Answered synchronously and without asking the network, because the alternative is a spinner while
 * something asynchronous decides whether to show a spinner — which is precisely the shape I-0173
 * names. The ordinary path pays nothing.
 *
 * The three facts it weighs:
 *
 * 1. **The app was served from the deployment**, and nothing else has been chosen since. There is
 *    nothing to ask and nothing to check.
 * 2. **There is no address at all.** A desktop build with nothing baked in and nothing chosen has
 *    nowhere to go. This is the first run ADR-050 creates, and it is the whole reason this screen
 *    is a primary one rather than an error.
 * 3. **A *chosen* address has never answered.** An address somebody typed is not a working one,
 *    and believing it without evidence is how an app spends twenty seconds looking busy before
 *    admitting it is talking to nothing. A *built-in* address is exempt: it was not typed, it
 *    cannot be improved by retyping, and its only failure mode is a deployment that is down —
 *    which `DeploymentWaitScreen` already handles without discarding the session.
 */
export function deploymentNeedsChoosing(): boolean {
  if (!readChosenDeploymentAddress() && servedFromItsOwnDeployment()) return false;
  const address = effectiveDeploymentAddress();
  if (!address) return true;
  /*
   * A build-time address is never asked about, not even once.
   *
   * The reachability test below exists for an address somebody *typed*, where "did this answer?"
   * is the question the screen was opened to settle. A baked-in address is not a guess in need of
   * confirmation — it is what this build is for, and the only reason it would fail is that the
   * deployment is down. That is `DeploymentWaitScreen`'s job, one step further along, where the
   * session is kept and a retry is offered; the connect screen would instead ask the reader to
   * supply an address they never chose and cannot improve on.
   *
   * Without this the first launch of every desktop build still routes to `/connect`, auto-probes,
   * and only then continues — a flash of a screen that must not appear at all.
   *
   * `readChosenDeploymentAddress()` is checked first above, so an explicit override still owns the
   * decision and still gets the reachability test it deserves.
   */
  if (!readChosenDeploymentAddress()) return false;
  /*
   * Both sides normalized, because the comparison decides whether to send somebody to the connect
   * screen. A build-time address spelled `HTTPS://Api.Example.com` would otherwise never equal the
   * normalized form recorded when it answered, and the gate would bounce to the connect screen on
   * every start — forever, since connecting successfully is exactly what writes the value being
   * compared.
   */
  return readReachedDeploymentAddress() !== normalizeDeploymentAddress(address);
}
